import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Clock,
  Users,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  Trash2,
  ClipboardPlus,
  X,
} from 'lucide-react';

import {
  fetchMeetingsThunk,
  fetchMeetingThunk,
  deleteMeetingThunk,
} from '../../../redux/admin/teamMeetingsActionThunks';
import { createTaskThunk } from '../../../redux/admin/tasksActionThunks';

/**
 * Team Meetings — admin-only archive of transcribed + diarized meeting
 * recordings (see Technical_Docs: transcription runs offline, on a GPU
 * machine, never on this server -- this page only stores/displays the
 * finished output). Distinct from Company Ops' Meeting Notes (a manual,
 * free-form decision log): this is a transcript-backed record with an
 * AI-generated summary. "Create Task" on any point of direction opens a
 * lightweight form that POSTs into the *same* Company Ops task system
 * (source_type='team_meeting', source_id=this meeting, source_excerpt=the
 * quote that prompted it) rather than a parallel one.
 */
const AdminTeamMeetingsComponent = ({ darkMode = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const items = useSelector((s) => s.teamMeetings.items);
  const detailById = useSelector((s) => s.teamMeetings.detailById);
  const loading = useSelector((s) => s.teamMeetings.loading.items);
  const error = useSelector((s) => s.teamMeetings.errors.items);
  const mutating = useSelector((s) => s.teamMeetings.loading.mutate);

  const [expandedId, setExpandedId] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [taskDraftFor, setTaskDraftFor] = useState(null); // { meetingId, excerpt } | null
  const [taskTitle, setTaskTitle] = useState('');
  const [taskError, setTaskError] = useState(null);
  const [taskSubmitting, setTaskSubmitting] = useState(false);

  const themeClasses = {
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hoverBg: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    inputBg: darkMode ? 'bg-gray-700' : 'bg-white',
    inputBorder: darkMode ? 'border-gray-600' : 'border-gray-300',
    buttonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
  };

  useEffect(() => {
    dispatch(fetchMeetingsThunk());
  }, [dispatch]);

  const toggleExpand = (meetingId) => {
    if (expandedId === meetingId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(meetingId);
    setShowTranscript(false);
    if (!detailById[meetingId]) dispatch(fetchMeetingThunk(meetingId));
  };

  const handleDelete = (meetingId) => {
    if (window.confirm(t('admin.teamMeetings.deleteConfirm'))) {
      dispatch(deleteMeetingThunk(meetingId));
    }
  };

  const openTaskDraft = (meetingId, excerpt) => {
    setTaskDraftFor({ meetingId, excerpt });
    setTaskTitle(excerpt.length > 120 ? excerpt.slice(0, 117) + '...' : excerpt);
    setTaskError(null);
  };

  const submitTaskDraft = async () => {
    if (!taskDraftFor || !taskTitle.trim()) return;
    setTaskSubmitting(true);
    setTaskError(null);
    const res = await dispatch(createTaskThunk({
      title: taskTitle.trim(),
      priority: 'medium',
      source_type: 'team_meeting',
      source_id: taskDraftFor.meetingId,
      source_excerpt: taskDraftFor.excerpt,
      assignee_ids: [],
    }));
    setTaskSubmitting(false);
    if (res.error) {
      setTaskError(res.payload || t('admin.teamMeetings.taskCreateFailed'));
    } else {
      setTaskDraftFor(null);
      setTaskTitle('');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const m = Math.round(seconds / 60);
    return t('admin.teamMeetings.durationMinutes', { count: m });
  };

  return (
    <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 shadow-sm`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.teamMeetings.heading')}</h2>
          <p className={`text-sm ${themeClasses.textMuted} mt-0.5`}>{t('admin.teamMeetings.subtitle')}</p>
        </div>
        {loading && <RefreshCw className={`h-4 w-4 animate-spin ${themeClasses.textMuted}`} />}
      </div>

      {error && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
          <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && !loading && (
          <p className={`text-sm ${themeClasses.textMuted} py-4 text-center`}>{t('admin.teamMeetings.noMeetings')}</p>
        )}
        {items.map((meeting) => {
          const isExpanded = expandedId === meeting.id;
          const detail = detailById[meeting.id];
          const pointsList = (detail?.points_of_direction || '')
            .split('\n')
            .map((line) => line.replace(/^[-*]\s*/, '').trim())
            .filter(Boolean);

          return (
            <div key={meeting.id} className={`rounded-lg border ${themeClasses.border} transition-colors`}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => toggleExpand(meeting.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(meeting.id); } }}
                className={`w-full flex items-start justify-between gap-3 p-4 text-left cursor-pointer ${themeClasses.hoverBg}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {isExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
                    <span className={`font-medium ${themeClasses.text}`}>{meeting.title}</span>
                  </div>
                  <div className={`flex items-center gap-3 text-xs ${themeClasses.textMuted} flex-wrap ml-6`}>
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(meeting.meeting_date).toLocaleDateString()}</span>
                    {meeting.duration_seconds && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {formatDuration(meeting.duration_seconds)}</span>}
                    {meeting.speaker_count && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {meeting.speaker_count}</span>}
                    {meeting.uploaded_by_username && <span>{t('admin.teamMeetings.uploadedBy')}: {meeting.uploaded_by_username}</span>}
                  </div>
                  {meeting.summary && <p className={`text-sm ${themeClasses.textSecondary} mt-2 ml-6 line-clamp-2`}>{meeting.summary}</p>}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(meeting.id); }}
                  disabled={mutating}
                  aria-label={t('admin.teamMeetings.deleteAria', { title: meeting.title })}
                  className={`p-1.5 rounded-lg flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/20' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {isExpanded && (
                <div className={`px-4 pb-4 border-t ${themeClasses.border} space-y-4`}>
                  {!detail ? (
                    <div className="pt-4"><RefreshCw className={`h-4 w-4 animate-spin ${themeClasses.textMuted}`} /></div>
                  ) : (
                    <>
                      {detail.summary && (
                        <div className="pt-4">
                          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${themeClasses.textMuted}`}>{t('admin.teamMeetings.summary')}</h3>
                          <p className={`text-sm ${themeClasses.textSecondary} whitespace-pre-line`}>{detail.summary}</p>
                        </div>
                      )}

                      {pointsList.length > 0 && (
                        <div>
                          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${themeClasses.textMuted}`}>{t('admin.teamMeetings.pointsOfDirection')}</h3>
                          <ul className="space-y-1.5">
                            {pointsList.map((point, i) => (
                              <li key={i} className={`flex items-start justify-between gap-2 text-sm ${themeClasses.textSecondary}`}>
                                <span className="flex-1">• {point}</span>
                                <button
                                  onClick={() => openTaskDraft(meeting.id, point)}
                                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg flex-shrink-0 ${darkMode ? 'text-blue-400 hover:bg-blue-900/20' : 'text-blue-600 hover:bg-blue-50'}`}
                                >
                                  <ClipboardPlus className="h-3.5 w-3.5" /> {t('admin.teamMeetings.createTask')}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div>
                        <button
                          onClick={() => setShowTranscript((s) => !s)}
                          className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${themeClasses.textMuted} hover:underline`}
                        >
                          {showTranscript ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {t('admin.teamMeetings.fullTranscript')}
                        </button>
                        {showTranscript && (
                          <pre className={`mt-2 p-3 rounded-lg text-xs whitespace-pre-wrap font-mono max-h-96 overflow-y-auto ${darkMode ? 'bg-gray-900/60 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                            {detail.transcript}
                          </pre>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {taskDraftFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setTaskDraftFor(null)}>
          <div
            className={`w-full max-w-lg rounded-xl border ${themeClasses.border} ${themeClasses.cardBg} p-5 shadow-xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className={`font-semibold ${themeClasses.text}`}>{t('admin.teamMeetings.createTask')}</h3>
              <button onClick={() => setTaskDraftFor(null)} aria-label={t('admin.companyOps.tasks.cancel')}>
                <X className={`h-4 w-4 ${themeClasses.textMuted}`} />
              </button>
            </div>
            <p className={`text-xs ${themeClasses.textMuted} mb-1`}>{t('admin.teamMeetings.whatWasSaid')}</p>
            <p className={`text-sm ${themeClasses.textSecondary} mb-3 p-2 rounded-lg italic ${darkMode ? 'bg-gray-900/40' : 'bg-gray-50'}`}>
              &ldquo;{taskDraftFor.excerpt}&rdquo;
            </p>
            <input
              type="text"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              placeholder={t('admin.companyOps.tasks.titlePlaceholder')}
              className={`w-full px-3 py-2 rounded-lg border ${themeClasses.inputBorder} ${themeClasses.inputBg} ${themeClasses.text} text-sm mb-3`}
            />
            {taskError && <p className="text-sm text-red-400 mb-3">{taskError}</p>}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setTaskDraftFor(null)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                {t('admin.companyOps.tasks.cancel')}
              </button>
              <button
                onClick={submitTaskDraft}
                disabled={taskSubmitting || !taskTitle.trim()}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${themeClasses.buttonPrimary}`}
              >
                {t('admin.companyOps.tasks.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeamMeetingsComponent;
